import {
  Component,
  OnInit,
  OnDestroy,
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
} from '@angular/core';
import { LoggerService } from '../../../services/logger.service';

import { DatePipe } from '@angular/common';
import { RouterModule } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CompanyService } from '../../../services/company.service';
import type { Company } from '../../../services/company.service';
import { StorefrontService } from '../../../services/storefront.service';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideCheck,
  lucideCircleCheck,
  lucideCopy,
  lucideExternalLink,
  lucideRadar,
  lucideUsers,
} from '@ng-icons/lucide';
import { HlmAlertImports } from '@spartan-ng/helm/alert';
import { HlmBadgeImports } from '@spartan-ng/helm/badge';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmItemImports } from '@spartan-ng/helm/item';
import { EmbedCodeComponent } from './components/embed-code/embed-code.component';

@Component({
  selector: 'app-integration-settings',
  imports: [
    DatePipe,
    RouterModule,
    NgIcon,
    HlmAlertImports,
    HlmBadgeImports,
    HlmButtonImports,
    HlmCardImports,
    HlmItemImports,
    EmbedCodeComponent,
  ],
  providers: [
    provideIcons({
      lucideCheck,
      lucideCircleCheck,
      lucideCopy,
      lucideExternalLink,
      lucideRadar,
      lucideUsers,
    }),
  ],
  templateUrl: './integration-settings.component.html',
  styleUrl: './integration-settings.component.css',
  changeDetection: ChangeDetectionStrategy.Eager,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class IntegrationSettingsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  company: Company | null = null;
  copiedKey: string | null = null;
  private copiedTimer: ReturnType<typeof setTimeout> | null = null;

  private markCopied(key: string) {
    if (this.copiedTimer) clearTimeout(this.copiedTimer);
    this.copiedKey = key;
    this.copiedTimer = setTimeout(() => {
      this.copiedKey = null;
    }, 2000);
  }

  constructor(
    private companyService: CompanyService,
    private logger: LoggerService,
    private storefrontService: StorefrontService,
  ) {}

  get effectiveStoreId(): string {
    if (this.company?.is_member && this.company.group_company_id) {
      return this.company.group_company_id;
    }
    return this.company?.public_id || 'DEINE_STORE_ID';
  }

  get effectiveSuburl(): string | null {
    if (this.company?.is_member && this.company.group_company_suburl) {
      return this.company.group_company_suburl;
    }
    return this.company?.suburl || null;
  }

  get storefrontUrl(): string {
    const suburl = this.effectiveSuburl;
    if (!suburl) return '';
    const protocol = this.storefrontService.getProtocol();
    const domain = this.storefrontService.getStorefrontBaseDomain();
    return `${protocol}${suburl}.${domain}`;
  }

  /** How the backend first saw the shop on the website. */
  get detectedVia(): string {
    return this.company?.integration_method === 'plugin' ? 'Plugin' : 'Link';
  }

  /** Platform guides for embedding the plugin. */
  readonly tutorials = [
    {
      name: 'Wix',
      hint: 'Custom Code einbinden',
      url: 'https://support.wix.com/en/article/wix-editor-embedding-custom-code-on-your-site',
    },
    {
      name: 'WordPress',
      hint: 'Code in Header einbinden',
      url: 'https://wordpress.com/support/adding-code-to-headers/',
    },
    {
      name: 'Squarespace',
      hint: 'Code-Injection nutzen',
      url: 'https://support.squarespace.com/hc/de/articles/205815908',
    },
    {
      name: 'Webflow',
      hint: 'Custom Code im Projekt',
      url: 'https://university.webflow.com/lesson/custom-code-in-the-head-and-body-tags',
    },
  ];

  copyUrl() {
    navigator.clipboard
      .writeText(this.storefrontUrl)
      .then(() => this.markCopied('url'))
      .catch(() => this.logger.error('Failed to copy URL to clipboard'));
  }

  ngOnInit() {
    this.companyService.company$.pipe(takeUntil(this.destroy$)).subscribe((company: Company) => {
      this.company = company;
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.copiedTimer) clearTimeout(this.copiedTimer);
  }
}
